import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import type {CopilotChatMessageFeedback} from '@github-ui/copilot-chat-types'
import {type FeedbackRef, type RatingOption, thumbsDownIcon, thumbsUpIcon, UserFeedback} from '@github-ui/user-feedback'
import {useImperativeHandle, useRef} from 'react'

import {useChatManager} from '../utils/CopilotChatManagerContext'

const ratingOptions: Array<RatingOption<CopilotChatMessageFeedback>> = [
  {
    name: 'Bad',
    value: 'NEGATIVE' as const,
    icon: thumbsDownIcon,
    color: 'bad',
  },
  {
    name: 'Good',
    value: 'POSITIVE' as const,
    icon: thumbsUpIcon,
    color: 'good',
  },
]

type Rating = (typeof ratingOptions)[number]['value']
export type FeedbackDialogRef = FeedbackRef<Rating>

type MessageFeedbackDialogProps = {
  messageId: string
  threadId: string
  returnFocusRef?: React.RefObject<HTMLElement | null>
  onSubmitted?: (rating: Rating) => void
  onClose?: () => void
  ref?: React.Ref<FeedbackDialogRef>
}

export function MessageFeedbackDialog({
  ref,
  messageId,
  threadId,
  returnFocusRef,
  onSubmitted,
  onClose,
}: MessageFeedbackDialogProps) {
  const manager = useChatManager()
  const feedbackDialogRef = useRef<FeedbackDialogRef>(null)

  useImperativeHandle(ref, () => ({
    openDialog: (rating?: Rating) => {
      feedbackDialogRef.current?.openDialog(rating)
    },
  }))

  const onSubmit = async (rating: Rating | null, text: string): Promise<string[]> => {
    const errors = []

    if (rating == null) {
      errors.push('Please select a rating.')
    }

    // max length based on hydro payload size limit
    // see https://github.com/github/copilot-api/blob/62323cfa4bfc31f808f8a457644f11bb706b8298/pkg/rest/chat.go#L382-L384
    const limit = 500
    if (text.length > limit) {
      errors.push(`Please keep your feedback within ${limit} characters or less.`)
    }

    if (errors.length > 0) {
      return errors
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rating = rating! // Make TS happy
    }

    try {
      const response = await manager.service.sendFeedback({
        feedback: rating,
        feedbackChoice: [], // This component does not offer users the ability to choose these (e.g. "UNHELPFUL", "INCORRECT", "NOT_TRUE", "POORLY_FORMATTED", etc.)
        messageId,
        threadId,
        textResponse: text,
      })

      if (response.ok) {
        onSubmitted?.(rating)
      } else {
        errors.push('An error occurred while submitting your feedback.')
      }
    } catch {
      errors.push('An error occurred while submitting your feedback.')
    }

    return errors
  }

  return (
    <UserFeedback
      title="Give additional feedback"
      ref={feedbackDialogRef}
      options={ratingOptions}
      onSubmit={onSubmit}
      onClose={onClose}
      requestID={copilotLocalStorage.getRequestID(messageId)}
      returnFocusRef={returnFocusRef}
    />
  )
}

MessageFeedbackDialog.displayName = 'MessageFeedbackDialog'
